import { AnalysisResultSectionRow, AnalysisResultStatus } from "../../../../services/AnalysisResultService";
import { LOM_SAMPLE_FIELDS } from "../resultOrderSelection";
import type { AnalysisSectionContentProps } from "./AnalysisSectionContent";
import { SampleAnalysisContent } from "./SampleAnalysisContent";
import { SampleValueColumn, SampleValueTable } from "./SampleValueTable";

/** Per-sample table under a LOM chip (Gradient, Porosity). Values are stored in row.sampleFields[field][sampleId]. */
function LomSampleTable({ section, row, normalizedSamples, onUpdateSection, readOnly }: Pick<AnalysisSectionContentProps, "section" | "normalizedSamples" | "onUpdateSection" | "readOnly"> & { row: AnalysisResultSectionRow }) {
  const columns: SampleValueColumn[] = LOM_SAMPLE_FIELDS[row.rowLabel].map(({ field, label, decimal }) => ({
    key: label,
    header: label,
    kind: decimal ? "decimal" : "text",
    getValue: sampleId => row.sampleFields?.[field]?.[sampleId],
    onChange: (sampleId, value) => onUpdateSection(section.analysisKey, current => ({
      ...current,
      status: AnalysisResultStatus.InProgress,
      rows: current.rows.map(item => item.rowLabel === row.rowLabel
        ? { ...item, sampleFields: { ...item.sampleFields, [field]: { ...item.sampleFields?.[field], [sampleId]: value } } }
        : item),
    })),
  }));

  return (
    <div className="pt-3">
      <SampleValueTable scrollAreaId={`analysis-result-${section.analysisKey}-${row.rowLabel}-grid`} normalizedSamples={normalizedSamples} columns={columns} readOnly={readOnly} />
    </div>
  );
}

export function ChipAnalysisContent(props: AnalysisSectionContentProps) {
  const { section, normalizedSamples, sectionFileInputRefs, uploadingSectionKey, onHandleRowFileSelect, onHandleSemRowFileUpload, onRemoveSemRowFile, onOpenFileGallery, onUpdateSection, readOnly = false } = props;
  const chips = (
    <div className="space-y-4 pb-5">
      {section.rows.map(row => {
        const key = `${section.analysisKey}:${row.rowLabel}`;
        const files = row.files ?? [];
        return (
          <div key={row.rowLabel}>
            <div className="grid grid-cols-[162px,minmax(0,1fr)] gap-3 bg-primary-dark/10 dark:bg-black/40 px-3.5 py-2 text-[14px]">
              <span className="col-span-2 break-words">{row.rowLabel}</span>
            </div>
            {section.analysisKey === "lom" && LOM_SAMPLE_FIELDS[row.rowLabel] && <LomSampleTable section={section} row={row} normalizedSamples={normalizedSamples} onUpdateSection={onUpdateSection} readOnly={readOnly} />}
            <div className="px-3.5 pt-3">
              <div className="grid min-h-8 grid-cols-[150px,minmax(0,1fr)] items-center gap-3 text-[12px]" onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); if (readOnly) return; void onHandleSemRowFileUpload(section.analysisKey, row.rowLabel, Array.from(event.dataTransfer.files)); }}>
                <span className="font-bold">Attach files</span>
                <div className="flex flex-wrap items-center gap-x-10 gap-y-2">
                  <input ref={element => { sectionFileInputRefs.current[key] = element; }} type="file" multiple className="hidden" onChange={event => { const selected = Array.from(event.target.files ?? []); event.target.value = ""; void onHandleSemRowFileUpload(section.analysisKey, row.rowLabel, selected); }} />
                  {files.map((file, index) => <span key={file.uuid} className="inline-flex items-center gap-3">
                    {!readOnly && <input type="checkbox" checked aria-label={`Remove ${file.fileName} from ${row.rowLabel}`} className="h-4 w-4 accent-oi-button-blue" onChange={() => onRemoveSemRowFile(section.analysisKey, row.rowLabel, file.uuid)} />}
                    <button type="button" onClick={() => onOpenFileGallery?.(files, index)}>{file.fileName}</button>
                  </span>)}
                  {!readOnly && <button type="button" aria-label={`Upload files for ${row.rowLabel}`} onClick={() => onHandleRowFileSelect(key)} className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-primary-light text-[22px] leading-none text-primary-dark dark:text-oi-dark-background">+</button>}
                  {uploadingSectionKey === key && <span role="status">Uploading files…</span>}
                </div>
              </div>
              <label className="mt-3 grid grid-cols-[150px,minmax(0,363px)] items-center gap-3">
                <span className="text-[12px] font-bold">Comment</span>
                <input disabled={readOnly} aria-label={`Comment for ${row.rowLabel}`} className="h-[37px] min-w-0 border border-primary-dark/50 dark:border-primary-light/50 bg-white dark:bg-oi-dark-background px-3 text-[14px] text-oi-button-blue dark:text-oi-powder-form-yellow outline-none placeholder:text-oi-button-blue dark:placeholder:text-oi-powder-form-yellow focus:border-oi-button-blue dark:focus:border-oi-powder-form-yellow" placeholder="-" value={row.comment ?? ""} onChange={event => onUpdateSection(section.analysisKey, current => ({ ...current, status: AnalysisResultStatus.InProgress, rows: current.rows.map(sample => sample.rowLabel === row.rowLabel ? { ...sample, comment: event.target.value } : sample) }))} />
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
  return <SampleAnalysisContent {...props} grid={chips} showSectionFields={false} />;
}
